// ======================== CreateLending.jsx (previous style) ========================
import { useEffect, useMemo, useState, useCallback } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";

/* ---------- axios ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
          ? ""
          : f.type === "date"
            ? ""
            : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      case "select":
        rule = yup.string();
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ---------- page ---------- */
export default function CreateLending() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [loadingOpts, setLoadingOpts] = useState(true);

  const [userOptions, setUserOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  const lsUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || "",
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw,
    );
  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? "",
  );

  const loadCustomers = async (selectedUserId) => {
    if (!selectedUserId) {
      setCustomerOptions([]);
      return;
    }

    try {
      const res = await api.get(`/api/customer/user/${selectedUserId}`, {
        params: { t: Date.now() },
      });

      const arr = (x) => (Array.isArray(x) ? x : []);

      const filtered = arr(res.data).filter(
        (c) => String(c?.type).toLowerCase() === "customer",
      );

      const mapped = filtered.map((c) => ({
        value: String(c.id),
        label: c.name || c.companyName,
      }));

      setCustomerOptions(mapped);
    } catch {
      setToast({
        open: true,
        msg: "Failed to load customers",
        sev: "error",
      });
      setCustomerOptions([]);
    }
  };

  const loadWarehouseItems = async (warehouseId) => {
    if (!warehouseId) {
      setItemOptions([]);
      return;
    }

    try {
      const res = await api.get("/api/store", {
        params: { warehouseId, t: Date.now() },
      });

      const arr = (x) => (Array.isArray(x) ? x : []);

      const mapped = arr(res.data).map((s) => ({
        value: String(s.itemId),
        label: `${s.Item?.name || `#${s.itemId}`} — SP: ${
          s.Item?.salePrice ?? 0
        }`,
      }));

      setItemOptions(mapped);
    } catch {
      setToast({
        open: true,
        msg: "Failed to load warehouse items",
        sev: "error",
      });
    }
  };

  const loadOptions = useCallback(async () => {
    setLoadingOpts(true);
    const t = Date.now();
    try {
      const [usersRes, warehousesRes] = await Promise.all([
        api.get("/api/user", {
          params: { t },
          headers: { "Cache-Control": "no-cache" },
        }),

        api.get("/api/warehouse", {
          params: { t },
          headers: { "Cache-Control": "no-cache" },
        }),
      ]);

      const arr = (x) => (Array.isArray(x) ? x : []);

      // ----- Users: mirror working sales style (robust label/value fallbacks)
      const usersRaw = arr(usersRes?.data);
      const uOpts = usersRaw
        .filter((u) => String(u?.Role?.name || "").toLowerCase() === "sales")
        .sort((a, b) =>
          String(
            a?.fullName ||
              a?.name ||
              a?.userName ||
              a?.username ||
              a?.email ||
              "",
          ).localeCompare(
            String(
              b?.fullName ||
                b?.name ||
                b?.userName ||
                b?.username ||
                b?.email ||
                "",
            ),
          ),
        )
        .map((u) => ({
          value: String(u?.id ?? u?.userId ?? u?._id), // value robust across id variants
          label:
            u?.fullName ||
            u?.name ||
            u?.userName || // you mentioned "userName": "kalebu"
            u?.username ||
            u?.email,
        }));

      // ----- Customers: request type=Customer + defensive client-side filter

      // ----- Warehouses
      const wOpts = arr(warehousesRes?.data)
        .sort((a, b) =>
          String(a?.name || a?.warehouseName || "").localeCompare(
            String(b?.name || b?.warehouseName || ""),
          ),
        )
        .map((w) => ({
          value: String(w?.id),
          label: w?.name || w?.warehouseName,
        }));

      setUserOptions(uOpts);

      setWarehouseOptions(wOpts);
      setLastRefreshed(Date.now());
    } catch (e) {
      setToast({
        open: true,
        msg: "Failed to load users/customers/items/warehouses",
        sev: "error",
      });
    } finally {
      setLoadingOpts(false);
    }
  }, []);

  // initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadOptions();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadOptions]);

  useEffect(() => {
    if (isSalesMan && currentUserId) {
      loadCustomers(currentUserId); // auto for sales
    }
  }, [isSalesMan, currentUserId]);

  // refresh on window focus
  useEffect(() => {
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOptions]);

  // custom event to refresh master data
  useEffect(() => {
    const handler = () => loadOptions();
    window.addEventListener("lending:refresh-master-data", handler);
    return () =>
      window.removeEventListener("lending:refresh-master-data", handler);
  }, [loadOptions]);

  const FIELDS = useMemo(() => {
    // For sales personnel: hide user field and use current user ID
    // For others: show user dropdown
    const userField = isSalesMan
      ? null
      : {
          name: "userId",
          label: "User",
          type: "select",
          span: 2,
          required: true,
          options: userOptions,
          key: `user-${lastRefreshed}`,
          onChange: async (e, bag) => {
            const userId = e.target.value;
            bag.setFieldValue("userId", userId);
            bag.setFieldValue("customerId", "");
            await loadCustomers(userId);
          },
          props: {
            disabled: loadingOpts || userOptions.length === 0,
            placeholder: loadingOpts ? "Loading..." : "Choose user",
          },
        };

    const fields = [
      userField, // This will be null for sales personnel
      {
        name: "customerId",
        label: "Customer",
        type: "autocomplete",
        span: 2,
        required: true,
        options: customerOptions,
        key: `customer-${lastRefreshed}`,
        props: {
          disabled: loadingOpts || customerOptions.length === 0,
          placeholder: loadingOpts ? "Loading..." : "Choose customer",
        },
      },
      {
        name: "warehouseId",
        label: "Warehouse",
        type: "select",
        span: 2,
        required: true,
        options: warehouseOptions,
        key: `warehouse-${lastRefreshed}`,
        onChange: async (e, bag) => {
          const warehouseId = e.target.value;
          bag.setFieldValue("warehouseId", warehouseId);
          bag.setFieldValue("itemId", ""); // reset item
          await loadWarehouseItems(warehouseId);
        },
        props: {
          disabled: loadingOpts || warehouseOptions.length === 0,
          placeholder: loadingOpts ? "Loading..." : "Choose warehouse",
        },
      },
      {
        name: "itemId",
        label: "Item",
        type: "select",
        span: 2,
        required: true,
        options:
          itemOptions.length === 0
            ? [{ value: "", label: "No items available" }]
            : itemOptions,
        key: `item-${lastRefreshed}`,
        props: {
          disabled: loadingOpts,
          placeholder: loadingOpts ? "Loading..." : "Choose item",
        },
      },
      {
        name: "quantity",
        label: "Quantity",
        type: "number",
        span: 2,
        required: true,
        min: 1,
        props: {
          placeholder: "Enter amount",
          inputProps: { step: 0.01, min: 0 },
          onKeyDown: (e) => {
            if (e.key === "-" || e.key === "e") {
              e.preventDefault();
            }
          },
        },
      },

      {
        name: "lendingDate",
        label: "Lending Date",
        type: "date",
        span: 2,
        required: true,
        props: { InputLabelProps: { shrink: true } },
      },
    ].filter((field) => field !== null); // Remove null fields

    return fields;
  }, [
    userOptions,
    customerOptions,
    itemOptions,
    warehouseOptions,
    loadingOpts,
    lastRefreshed,
    isSalesMan,
  ]);

  // Base schema from field metadata, with stricter quantity integer rule
  const SCHEMA = useMemo(() => {
    const base = buildSchema(FIELDS);
    return base.shape({
      quantity: yup
        .number()
        .typeError("must be a number")

        .min(1, "min 1")
        .required("required"),
    });
  }, [FIELDS]);

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, resetForm },
  ) => {
    // For sales personnel: use current user ID, for others: use selected userId
    const userId = isSalesMan
      ? Number(currentUserId)
      : values.userId
        ? Number(values.userId)
        : undefined;

    const payload = {
      userId: userId,
      customerId: values.customerId ? Number(values.customerId) : undefined,
      itemId: values.itemId ? Number(values.itemId) : undefined,
      quantity: Number(values.quantity),
      warehouseId: values.warehouseId ? Number(values.warehouseId) : undefined,
    };

    if (values.lendingDate) {
      const d = new Date(values.lendingDate);
      if (!isNaN(d.getTime())) payload.lendingDate = d.toISOString();
    }

    const { data, error } = await postJSON("/api/lending", payload);
    setSubmitting(false);

    if (error) {
      if (typeof error === "object" && !Array.isArray(error)) setErrors(error);
      setToast({ open: true, msg: "Failed to create lending", sev: "error" });
      return;
    }

    const msg =
      data?.message || "Lending created successfully and item stock updated";
    setToast({ open: true, msg, sev: "success" });
    resetForm();

    // refresh master data after success
    await loadOptions();
  };

  const LoadingBar = loadingOpts ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CircularProgress size={18} />
      <span style={{ fontSize: 14, opacity: 0.8 }}>Loading dropdowns…</span>
    </Box>
  ) : null;

  const formKey = `${lastRefreshed}`;

  return (
    <>
      <SimpleForm
        key={formKey}
        title="CREATE LENDING"
        subtitle="Record a new lending and update stock"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Lending"
        columns={4}
        // If SimpleForm wraps Formik, consider enableReinitialize inside it.
      />

      {LoadingBar}

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
